import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap } from 'lucide-react';

const PriceCard = ({ plan, isHighlighted }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`price-card ${isHighlighted ? 'price-card-highlight' : ''}`}
    >
      {isHighlighted && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-primary-600 text-white">
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
        <p className="text-gray-600">{plan.description}</p>
      </div>

      <div className="mb-8">
        {typeof plan.price === 'number' ? (
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
            <span className="text-gray-600 ml-2">/month</span>
          </div>
        ) : (
          <div className="text-4xl font-bold text-gray-900">{plan.price}</div>
        )}
      </div>

      <ul className="mb-8 space-y-4">
        {plan.features.map((feature, index) => (
          <motion.li
            key={feature}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="feature-list-item"
          >
            <Check className="feature-icon" />
            <span className="text-gray-600">{feature}</span>
          </motion.li>
        ))}
      </ul>

      <motion.button
        whileHover={{ scale: 1.02 }}
        className={isHighlighted ? 'btn-primary w-full' : 'btn-secondary w-full'}
      >
        {plan.name === "Enterprise" ? "Contact Sales" : "Start Free Trial"}
      </motion.button>
    </motion.div>
  );
};

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: "Starter",
      price: isAnnual ? 39 : 49,
      description: "Perfect for small businesses just getting started",
      features: [
        "AI-powered responses (100/month)",
        "Basic appointment scheduling",
        "Customer message management",
        "Email support",
        "Basic analytics"
      ]
    },
    {
      name: "Professional",
      price: isAnnual ? 89 : 99,
      description: "Ideal for growing businesses",
      features: [
        "Unlimited AI responses",
        "Advanced appointment system",
        "Multi-platform integration",
        "Priority support",
        "Advanced analytics",
        "Custom automation rules",
        "Team collaboration"
      ]
    },
    {
      name: "Enterprise",
      price: "Custom",
      description: "For large businesses with custom needs",
      features: [
        "Custom AI training",
        "Dedicated account manager",
        "API access",
        "Custom integrations",
        "SLA guarantees",
        "24/7 phone support",
        "Custom reporting"
      ]
    }
  ];

  return (
    <section className="py-20 gradient-light" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold heading-gradient mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Choose the plan that's right for your business
          </p>

          {/* Pricing Toggle */}
          <div className="flex items-center justify-center mb-8">
            <span className={`mr-3 ${!isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-primary-600 transition-colors"
            >
              <motion.span
                initial={false}
                animate={{ x: isAnnual ? 20 : 2 }}
                className="inline-block h-4 w-4 transform rounded-full bg-white transition"
              />
            </button>
            <span className={`ml-3 flex items-center ${isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
              Annually
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Save 20%
              </span>
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <PriceCard
              key={plan.name}
              plan={plan}
              isHighlighted={index === 1}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600">
            All plans include a 14-day free trial. No credit card required.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;